import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Collapse,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  MaterialGrade,
  Surface,
  MetalCompositionCreate,
  MechanicalPropertyCreate,
  MaterialType,
} from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import MetalCompositionForm from '../components/MetalCompositionForm';
import MechanicalPropertyForm from '../components/MechanicalPropertyForm';
import MaterialLexwareTable from '../components/MaterialLexwareTable';
import LieferantenTable from '../components/LieferantenTable';
import MaterialNormsTable from '../components/MaterialNormsTable';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../components/ToastProvider';
import SortableHeader from '../components/SortableHeader';
import { useTableSort } from '../utils/tableSort';
import { apiErrorMessage } from '../utils/apiError';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>{value === index && <Box sx={{ p: 3 }}>{children}</Box>}</div>
  );
};

const MasterDataPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [materialGrades, setMaterialGrades] = useState<MaterialGrade[]>([]);
  const [surfaces, setSurfaces] = useState<Surface[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toastError } = useToast();
  const gradesSort = useTableSort<'name' | 'material_type' | 'description' | 'metals' | 'created_at'>(
    'name',
    'asc',
    ['created_at']
  );
  const surfacesSort = useTableSort<'name' | 'description' | 'created_at'>('name', 'asc', [
    'created_at',
  ]);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'grade' | 'surface'>('grade');
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editItem, setEditItem] = useState<MaterialGrade | Surface | null>(null);

  // Form states
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formMaterialType, setFormMaterialType] = useState<MaterialType>('EDELSTAHL');
  const [formMetalCompositions, setFormMetalCompositions] = useState<MetalCompositionCreate[]>([]);
  const [formMechanicalProperties, setFormMechanicalProperties] = useState<
    MechanicalPropertyCreate[]
  >([]);

  // UI states
  const [expandedGrades, setExpandedGrades] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadMasterData();
  }, []);

  // Check if user has access to master data
  if (user?.role !== 'KALOTTE') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nur KALOTTE-Benutzer haben Zugriff auf die Stammdatenverwaltung.
        </Alert>
      </Box>
    );
  }

  const loadMasterData = async () => {
    try {
      const [gradesData, surfacesData] = await Promise.all([
        apiService.getMaterialGrades(),
        apiService.getSurfaces(),
      ]);
      setMaterialGrades(gradesData);
      setSurfaces(surfacesData);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Stammdaten');
      console.error('Error loading master data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (
    type: 'grade' | 'surface',
    mode: 'create' | 'edit',
    item?: MaterialGrade | Surface
  ) => {
    setDialogType(type);
    setDialogMode(mode);
    setEditItem(item || null);
    setFormName(item?.name || '');
    setFormDescription(item?.description || '');

    if (type === 'grade' && item && 'metal_compositions' in item) {
      setFormMaterialType(item.material_type || 'EDELSTAHL');
      setFormMetalCompositions(
        item.metal_compositions.map((comp) => ({
          metal_name: comp.metal_name,
          min_percentage: comp.min_percentage,
          max_percentage: comp.max_percentage,
        }))
      );

      if (item.mechanical_properties?.length) {
        setFormMechanicalProperties(
          item.mechanical_properties.map((prop) => ({
            property_name: prop.property_name,
            min_value: prop.min_value ?? undefined,
            max_value: prop.max_value ?? undefined,
            unit: prop.unit ?? undefined,
          }))
        );
      } else {
        setFormMechanicalProperties([]);
      }
    } else {
      setFormMetalCompositions([]);
      setFormMechanicalProperties([]);
    }

    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditItem(null);
    setFormName('');
    setFormDescription('');
    setFormMaterialType('EDELSTAHL');
    setFormMetalCompositions([]);
    setFormMechanicalProperties([]);
  };

  const handleSave = async () => {
    try {
      if (dialogType === 'grade') {
        const gradeData = {
          name: formName,
          description: formDescription || undefined,
          material_type: formMaterialType,
          metal_compositions: formMetalCompositions,
          mechanical_properties: formMechanicalProperties,
        };

        if (dialogMode === 'create') {
          await apiService.createMaterialGrade(gradeData);
        } else if (editItem) {
          await apiService.updateMaterialGrade(editItem.id, gradeData);
        }
      } else {
        const surfaceData = {
          name: formName,
          description: formDescription || undefined,
        };

        if (dialogMode === 'create') {
          await apiService.createSurface(surfaceData);
        } else if (editItem) {
          await apiService.updateSurface(editItem.id, surfaceData);
        }
      }

      handleCloseDialog();
      loadMasterData();
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Fehler beim Speichern'));
      console.error('Error saving:', err);
    }
  };

  const handleDelete = async (type: 'grade' | 'surface', id: number) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Eintrag löschen möchten?')) {
      return;
    }

    try {
      if (type === 'grade') {
        await apiService.deleteMaterialGrade(id);
      } else {
        await apiService.deleteSurface(id);
      }
      loadMasterData();
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Fehler beim Löschen'));
      console.error('Error deleting:', err);
    }
  };

  const toggleGradeExpansion = (gradeId: number) => {
    const newExpanded = new Set(expandedGrades);
    if (newExpanded.has(gradeId)) {
      newExpanded.delete(gradeId);
    } else {
      newExpanded.add(gradeId);
    }
    setExpandedGrades(newExpanded);
  };

  const sortedGrades = gradesSort.sortRows(materialGrades, {
    name: (grade) => grade.name,
    material_type: (grade) => grade.material_type,
    description: (grade) => grade.description,
    metals: (grade) => grade.metal_compositions.length,
    created_at: (grade) => grade.created_at,
  });
  const sortedSurfaces = surfacesSort.sortRows(surfaces, {
    name: (surface) => surface.name,
    description: (surface) => surface.description,
    created_at: (surface) => surface.created_at,
  });

  if (loading) {
    return <LoadingSpinner message="Stammdaten werden geladen..." />;
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Stammdaten
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Materialgüten" />
            <Tab label="Oberflächen" />
            <Tab label="Material Lexware" />
            <Tab label="Lieferanten" />
            <Tab label="Werkstoffnormen" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">Materialgüten</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('grade', 'create')}
            >
              Neue Materialgüte
            </Button>
          </Box>

          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', py: 0.75 } }}>
              <TableHead>
                <TableRow>
                  <TableCell width="40px"></TableCell>
                  <SortableHeader column="name" label="Name" sort={gradesSort.sort} order={gradesSort.order} onSort={gradesSort.onSort} />
                  <SortableHeader column="material_type" label="Typ" sort={gradesSort.sort} order={gradesSort.order} onSort={gradesSort.onSort} />
                  <SortableHeader column="description" label="Beschreibung" sort={gradesSort.sort} order={gradesSort.order} onSort={gradesSort.onSort} />
                  <SortableHeader column="metals" label="Metalle" sort={gradesSort.sort} order={gradesSort.order} onSort={gradesSort.onSort} />
                  <SortableHeader column="created_at" label="Erstellt am" sort={gradesSort.sort} order={gradesSort.order} onSort={gradesSort.onSort} />
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedGrades.map((grade) => (
                  <React.Fragment key={grade.id}>
                    <TableRow>
                      <TableCell>
                        {grade.metal_compositions.length > 0 && (
                          <IconButton size="small" onClick={() => toggleGradeExpansion(grade.id)}>
                            {expandedGrades.has(grade.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{grade.name}</TableCell>
                      <TableCell>
                        {grade.material_type === 'EDELSTAHL'
                          ? 'Edelstahl'
                          : grade.material_type === 'ALU'
                            ? 'Aluminium'
                            : grade.material_type === 'HSF_PP'
                              ? 'HSF-PP'
                              : grade.material_type === 'HSF_PE'
                                ? 'HSF-PE'
                                : grade.material_type}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {grade.description || '—'}
                      </TableCell>
                      <TableCell>
                        {grade.metal_compositions.length === 0
                          ? '—'
                          : `${grade.metal_compositions
                              .slice(0, 4)
                              .map((c) => c.metal_name)
                              .join(', ')}${
                              grade.metal_compositions.length > 4
                                ? ` +${grade.metal_compositions.length - 4}`
                                : ''
                            }`}
                      </TableCell>
                      <TableCell>
                        {new Date(grade.created_at).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('grade', 'edit', grade)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete('grade', grade.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {(grade.metal_compositions.length > 0 ||
                      (grade.mechanical_properties && grade.mechanical_properties.length > 0)) && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                          <Collapse in={expandedGrades.has(grade.id)}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              {grade.metal_compositions.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Metall-Zusammensetzung:
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {grade.metal_compositions.map((comp, index) => (
                                      <Box
                                        key={index}
                                        sx={{
                                          p: 1.5,
                                          border: '1px solid',
                                          borderColor: 'grey.300',
                                          borderRadius: 1,
                                          minWidth: '150px',
                                          bgcolor: 'background.paper',
                                        }}
                                      >
                                        <Typography variant="body2" fontWeight="medium">
                                          {comp.metal_name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                          {comp.min_percentage}% - {comp.max_percentage}%
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {grade.mechanical_properties &&
                                grade.mechanical_properties.length > 0 && (
                                  <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Mechanische Eigenschaften:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                      {grade.mechanical_properties.map((prop, index) => (
                                        <Box
                                          key={index}
                                          sx={{
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: 'secondary.main',
                                            borderRadius: 1,
                                            minWidth: '150px',
                                            bgcolor: 'background.paper',
                                          }}
                                        >
                                          <Typography variant="body2" fontWeight="medium">
                                            {prop.property_name}
                                          </Typography>
                                          <Typography variant="caption" color="textSecondary">
                                            {prop.min_value !== null
                                              ? `min. ${prop.min_value}`
                                              : ''}
                                            {prop.min_value !== null && prop.max_value !== null
                                              ? ' - '
                                              : ''}
                                            {prop.max_value !== null
                                              ? `max. ${prop.max_value}`
                                              : ''}
                                            {prop.unit && ` ${prop.unit}`}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">Oberflächen</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('surface', 'create')}
            >
              Neue Oberfläche
            </Button>
          </Box>

          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', py: 0.75 } }}>
              <TableHead>
                <TableRow>
                  <SortableHeader column="name" label="Name" sort={surfacesSort.sort} order={surfacesSort.order} onSort={surfacesSort.onSort} />
                  <SortableHeader column="description" label="Beschreibung" sort={surfacesSort.sort} order={surfacesSort.order} onSort={surfacesSort.onSort} />
                  <SortableHeader column="created_at" label="Erstellt am" sort={surfacesSort.sort} order={surfacesSort.order} onSort={surfacesSort.onSort} />
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSurfaces.map((surface) => (
                  <TableRow key={surface.id}>
                    <TableCell>{surface.name}</TableCell>
                    <TableCell
                      sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {surface.description || '—'}
                    </TableCell>
                    <TableCell>
                      {new Date(surface.created_at).toLocaleDateString('de-DE')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog('surface', 'edit', surface)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete('surface', surface.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <MaterialLexwareTable userRole={user?.role || ''} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <LieferantenTable userRole={user?.role || ''} />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <MaterialNormsTable userRole={user?.role || ''} />
        </TabPanel>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth={dialogType === 'grade' ? 'md' : 'xs'} fullWidth scroll="paper">
        <DialogTitle>
          {dialogMode === 'create' ? 'Neu erstellen' : 'Bearbeiten'}:{' '}
          {dialogType === 'grade' ? 'Materialgüte' : 'Oberfläche'}
        </DialogTitle>
        <DialogContent>
          <TextField
            required
            fullWidth
            label="Name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Beschreibung"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            margin="normal"
            multiline
            rows={2}
          />

          {dialogType === 'grade' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Materialtyp</InputLabel>
              <Select
                value={formMaterialType}
                label="Materialtyp"
                onChange={(e) => setFormMaterialType(e.target.value as MaterialType)}
              >
                <MenuItem value="EDELSTAHL">Edelstahl</MenuItem>
                <MenuItem value="ALU">Aluminium</MenuItem>
                <MenuItem value="HSF_PP">HSF-PP</MenuItem>
                <MenuItem value="HSF_PE">HSF-PE</MenuItem>
              </Select>
            </FormControl>
          )}

          {dialogType === 'grade' && (
            <>
              <Box sx={{ mt: 3 }}>
                <MetalCompositionForm
                  compositions={formMetalCompositions}
                  onChange={setFormMetalCompositions}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <MechanicalPropertyForm
                  mechanicalProperties={formMechanicalProperties}
                  onChange={setFormMechanicalProperties}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formName.trim()}>
            {dialogMode === 'create' ? 'Erstellen' : 'Aktualisieren'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterDataPage;
